const MAX_SAFE_INTEGER = 9007199254740991;


/* ============================================================
 * INTEGER
 * ============================================================ */

export const normalizeInteger = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();

  if (!text) {
    return null;
  }

  const cleaned = text.replace(/,/g, "");

  if (!/^-?\d+$/.test(cleaned)) {
    return null;
  }

  const number = Number(cleaned);

  if (!isFinite(number)) {
    return null;
  }

  if (Math.floor(number) !== number) {
    return null;
  }

  if (Math.abs(number) > MAX_SAFE_INTEGER) {
    return null;
  }

  return number;
};


/* ============================================================
 * FLOAT
 * ============================================================ */

export const normalizeFloat = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const text = String(value).trim();

    if (!text) {
    return null;
    }

    // Valid float formats:
    // 123
    // 123.
    // 123.45
    // .45
    // -123
    // -123.
    // -123.45
    // -.45
    const validFloat = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;

    if (!validFloat.test(text)) {
    return null;
    }

    const number = Number(text);

    // ES2015-compatible finite-number check.
    if (!isFinite(number)) {
    return null;
    }

    return number;
};


/* ============================================================
 * BOOLEAN
 * ============================================================ */

export const normalizeBoolean = (value) => {
  if (value === true || value === false) {
    return value;
  }

  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim().toLowerCase();

  if (text === "true") {
    return true;
  }

  if (text === "false") {
    return false;
  }

  return null;
};


/* ============================================================
 * LIST ITEM
 * ============================================================ */

export const normalizeListItem = (value) => {
    const items = [];

    const addItem = (valueToAdd) => {
      if (
        valueToAdd === null ||
        valueToAdd === undefined
      ) {
        return;
      }
  
      let item = String(valueToAdd).trim();
  
      if (!item) {
        return;
      }
  
      /*
       * Remove quotes surrounding an item.
       */
      item = item
        .replace(/^["']+/, "")
        .replace(/["']+$/, "")
        .trim();
  
      if (!item) {
        return;
      }
  
      /*
       * Prevent duplicate list items.
       */
      if (items.indexOf(item) === -1) {
        items.push(item);
      }
    };
  
  
    /*
     * Remove all escape/control artifacts.
     *
     * Handles repeated levels such as:
     *
     * \r
     * \n
     * \\r
     * \\n
     * \\\\r
     * \\\\n
     * \"
     * \\"
     * etc.
     */
    const cleanEscapes = (input) => {
      let result = String(input);
  
      for (let i = 0; i < 30; i += 1) {
        const previous = result;
  
        result = result
          /* Actual line breaks */
          .replace(/\r\n/g, "")
          .replace(/\n/g, "")
          .replace(/\r/g, "")
  
          /* Escaped line breaks */
          .replace(/\\r\\n/g, "")
          .replace(/\\n/g, "")
          .replace(/\\r/g, "")
  
          /* Escaped quotes */
          .replace(/\\"/g, '"')
  
          /* Repeated backslashes */
          .replace(/\\\\/g, "\\")
  
          .trim();
  
        if (result === previous) {
          break;
        }
      }
  
      return result;
    };
  
  
    const extract = (current, depth) => {
      /*
       * Prevent infinite recursion on malformed input.
       */
      if (
        depth > 30 ||
        current === null ||
        current === undefined
      ) {
        return;
      }
  
  
      /*
       * Array.
       */
      if (Array.isArray(current)) {
        current.forEach((item) => {
          extract(item, depth + 1);
        });
  
        return;
      }
  
  
      /*
       * Object.
       */
      if (typeof current === "object") {
        Object.keys(current).forEach((key) => {
          extract(current[key], depth + 1);
        });
  
        return;
      }
  
  
      let text = String(current).trim();
  
      if (!text) {
        return;
      }
  
  
      /*
       * Repeatedly unwrap JSON arrays/strings.
       */
      for (let i = 0; i < 30; i += 1) {
        text = cleanEscapes(text);
  
        if (!text) {
          return;
        }
  
        const first = text.charAt(0);
        const last = text.charAt(text.length - 1);
  
        const isArray =
          first === "[" &&
          last === "]";
  
        const isQuotedString =
          first === '"' &&
          last === '"';
  
        if (!isArray && !isQuotedString) {
          break;
        }
  
        try {
          const parsed = JSON.parse(text);
  
          /*
           * Nested array.
           */
          if (Array.isArray(parsed)) {
            extract(parsed, depth + 1);
            return;
          }
  
          /*
           * Nested string.
           */
          if (typeof parsed === "string") {
            if (parsed === text) {
              break;
            }
  
            text = parsed;
            continue;
          }
  
          /*
           * Nested object.
           */
          if (
            parsed !== null &&
            typeof parsed === "object"
          ) {
            extract(parsed, depth + 1);
            return;
          }
  
          break;
        } catch (error) {
          /*
           * Not valid JSON at this level.
           * Continue with normal cleanup.
           */
          break;
        }
      }
  
  
      /*
       * Final escape cleanup.
       */
      text = cleanEscapes(text).trim();
  
  
      /*
       * Remove surrounding square brackets repeatedly.
       */
      for (let i = 0; i < 30; i += 1) {
        const trimmed = text.trim();
  
        if (
          trimmed.length >= 2 &&
          trimmed.charAt(0) === "[" &&
          trimmed.charAt(trimmed.length - 1) === "]"
        ) {
          text = trimmed
            .substring(1, trimmed.length - 1)
            .trim();
  
          continue;
        }
  
        break;
      }
  
  
      /*
       * Remove remaining formatting characters.
       *
       * IMPORTANT:
       * "/" is intentionally preserved.
       */
      text = text
        .split("[")
        .join("")
        .split("]")
        .join("")
        .split('"')
        .join("")
        .split("'")
        .join("")
        .replace(/\\/g, "")
        .trim();
  
  
      if (!text) {
        return;
      }
  
  
      /*
       * Comma is the ONLY list separator.
       */
      text.split(",").forEach((item) => {
        addItem(item);
      });
    };
  
  
    extract(value, 0);
  
    return items.join(",");
};


/* ============================================================
 * FINAL NORMALIZER
 * ============================================================ */

export const normalizeValue = (value, dataType, cardinalitySet = false) => {
  if (value === null || value === undefined) {
    return null;
  }

  const originalType = String(dataType || "")
    .trim()
    .toLowerCase();

    if (cardinalitySet === true) {
      if (originalType === "integer" || originalType === "float") {
        return String(value).replace(/[a-zA-Z]/g, "");
      }
      dataType = "string";
    }

  const type = String(dataType || "")
    .trim()
    .toLowerCase();

  switch (type) {
    case "integer":
      return normalizeInteger(value);

    case "float":
      return normalizeFloat(value);

    case "boolean":
      return normalizeBoolean(value);

    case "list_item":
      return normalizeListItem(value);

    default:
      return value;
  }
};


/* ============================================================
 * LIVE INPUT SANITIZER
 * ============================================================ */

export const sanitizeInput = (value, dataType, cardinalitySet = false) => {
  if (value === null || value === undefined) {
    return "";
  }

    const originalType = String(dataType || "")
    .trim()
    .toLowerCase();

    if (cardinalitySet === true) {
      if (originalType === "integer" || originalType === "float") {
        return String(value).replace(/[a-zA-Z]/g, "");
      }
      dataType = "string";
    }

  const type = String(dataType || "")
    .trim()
    .toLowerCase();


  switch (type) {

    /* --------------------------------------------------------
     * INTEGER
     * -------------------------------------------------------- */

    case "integer": {
      let result = String(value);

      // Only digits and minus.
      result = result.replace(/[^\d-]/g, "");

      // Minus only at beginning.
      if (result.indexOf("-") !== -1) {
        result =
          "-" +
          result.replace(/-/g, "");
      }

      // Keep comma while typing.
      return result;
    }


    /* --------------------------------------------------------
     * FLOAT
     * -------------------------------------------------------- */

    case "float": {
        if (value === null || value === undefined) {
            return "";
        }
    
        var result = String(value);
    
        // Convert comma decimal separator to dot.
        result = result.replace(/,/g, ".");
    
        // Keep only digits, dot and minus.
        result = result.replace(/[^\d.-]/g, "");
    
        // Preserve a negative sign only when it was present.
        var isNegative = result.indexOf("-") === 0;
    
        // Remove all minus signs.
        result = result.replace(/-/g, "");
    
        // Keep only the first decimal point.
        var dotIndex = result.indexOf(".");
    
        if (dotIndex !== -1) {
        result =
            result.substring(0, dotIndex + 1) +
            result.substring(dotIndex + 1).replace(/\./g, "");
        }
    
        // Restore minus only at the beginning.
        if (isNegative) {
        result = "-" + result;
        }
    
        return result;
    }


    /* --------------------------------------------------------
     * BOOLEAN
     * -------------------------------------------------------- */

    case "boolean": {
      let result = String(value);

      // Only letters.
      result = result.replace(/[^a-zA-Z]/g, "");

      // Maximum useful value is "false" / "true".
      result = result
        .toLowerCase()
        .substring(0, 5);

      return result;
    }


    /* --------------------------------------------------------
     * LIST ITEM
     * -------------------------------------------------------- */

    case "list_item": {
        let result = String(value);

        result = result
            .split("[")
            .join("")
            .split("]")
            .join("");


        /*
        * Remove quotes.
        */
        result = result
            .split('"')
            .join("")
            .split("'")
            .join("");


        /*
        * Remove actual line breaks.
        */
        result = result
            .replace(/\r\n/g, "")
            .replace(/\n/g, "")
            .replace(/\r/g, "");


        /*
        * Remove escaped line breaks.
        */
        result = result
            .replace(/\\r\\n/g, "")
            .replace(/\\n/g, "")
            .replace(/\\r/g, "");


        /*
        * Remove escaped quotes.
        */
        result = result.replace(/\\"/g, "");


        /*
        * Remove remaining backslashes.
        *
        * This does NOT remove "/".
        *
        * T89/B -> T89/B
        */
        result = result.replace(/\\/g, "");


        /*
        * Normalize comma-separated values.
        */
        /* result = result
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "")
            .join(",");*/
        return result;
    }


    default:
      return value;
  }
};
